import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  Button,
  Chip,
  Collapse,
  IconButton,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  Bookmark as BookmarkIcon,
  BookmarkBorder as BookmarkBorderIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DocumentFilters, DocumentCategory, SearchScope } from '@nvgc/shared';

interface FilterPanelProps {
  filters: DocumentFilters;
  onFiltersChange: (filters: DocumentFilters) => void;
  onApplyFilters?: () => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

interface FilterPreset {
  id: string;
  name: string;
  description: string;
  filters: DocumentFilters;
}

const DEFAULT_PRESETS: FilterPreset[] = [
  {
    id: 'recent-regulations',
    name: 'Recent Regulations',
    description: 'Regulations from the last 6 months',
    filters: {
      categories: ['Regulation'],
      dateRange: {
        start: new Date(Date.now() - 6 * 30 * 24 * 60 * 60 * 1000),
        end: new Date()
      },
      documentSource: 'public'
    }
  },
  {
    id: 'all-statutes',
    name: 'All Statutes',
    description: 'All Nevada gaming statutes',
    filters: {
      categories: ['Statute'],
      documentSource: 'public'
    }
  },
  {
    id: 'technical-standards',
    name: 'Technical Standards',
    description: 'Current technical standards and requirements',
    filters: {
      categories: ['Technical Standard'],
      documentSource: 'public'
    }
  },
  {
    id: 'my-documents',
    name: 'My Documents',
    description: 'Only my uploaded documents',
    filters: {
      categories: [],
      documentSource: 'private'
    }
  }
];

const DOCUMENT_CATEGORIES: { value: DocumentCategory; label: string; description: string }[] = [
  { value: 'Statute', label: 'Statutes', description: 'Nevada gaming statutes and laws' },
  { value: 'Regulation', label: 'Regulations', description: 'Gaming commission regulations' },
  { value: 'Technical Standard', label: 'Technical Standards', description: 'Technical requirements and standards' },
  { value: 'News', label: 'News & Updates', description: 'Recent news and regulatory updates' },
  { value: 'User Document', label: 'User Documents', description: 'Uploaded documents and forms' }
];

export const FilterPanel: React.FC<FilterPanelProps> = ({
  filters,
  onFiltersChange,
  onApplyFilters,
  isCollapsed = false,
  onToggleCollapse
}) => {
  const [localFilters, setLocalFilters] = useState<DocumentFilters>(filters);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [presetMenuAnchor, setPresetMenuAnchor] = useState<null | HTMLElement>(null);
  const [savedPresets, setSavedPresets] = useState<FilterPreset[]>([]);

  // Load saved presets from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('nvgc-filter-presets');
    if (saved) {
      try {
        setSavedPresets(JSON.parse(saved));
      } catch (error) {
        console.error('Failed to load saved presets:', error);
      }
    }
  }, []);

  // Update local filters when props change
  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleCategoryChange = (category: DocumentCategory, checked: boolean) => {
    const newCategories = checked
      ? [...localFilters.categories, category]
      : localFilters.categories.filter(c => c !== category);
    
    setLocalFilters({
      ...localFilters,
      categories: newCategories
    });
  };

  const handleDocumentSourceChange = (source: SearchScope) => {
    setLocalFilters({
      ...localFilters,
      documentSource: source
    });
  };

  const handleDateRangeChange = (field: 'start' | 'end', date: Date | null) => {
    if (!date) {
      setLocalFilters({
        ...localFilters,
        dateRange: undefined
      });
      return;
    }

    const currentRange = localFilters.dateRange || {
      start: new Date(),
      end: new Date()
    };

    setLocalFilters({
      ...localFilters,
      dateRange: {
        ...currentRange,
        [field]: date
      }
    });
  };

  const handleApplyFilters = () => {
    onFiltersChange(localFilters);
    onApplyFilters?.();
  };

  const handleClearFilters = () => {
    const clearedFilters: DocumentFilters = {
      categories: [],
      documentSource: 'all'
    };
    setLocalFilters(clearedFilters);
    onFiltersChange(clearedFilters);
  };

  const handleApplyPreset = (preset: FilterPreset) => {
    setLocalFilters(preset.filters);
    onFiltersChange(preset.filters);
    setPresetMenuAnchor(null);
  };

  const handleSavePreset = () => {
    const name = prompt('Enter a name for this filter preset:');
    if (!name) return;

    const newPreset: FilterPreset = {
      id: `custom-${Date.now()}`,
      name,
      description: 'Custom filter preset',
      filters: localFilters
    };

    const updatedPresets = [...savedPresets, newPreset];
    setSavedPresets(updatedPresets);
    localStorage.setItem('nvgc-filter-presets', JSON.stringify(updatedPresets));
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (localFilters.categories.length > 0) count++;
    if (localFilters.dateRange) count++;
    if (localFilters.documentSource !== 'all') count++;
    return count;
  };

  const filterContent = (
    <Box sx={{ p: 2 }}>
      {/* Filter Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FilterListIcon color="primary" />
          <Typography variant="h6">
            Filters
          </Typography>
          {getActiveFilterCount() > 0 && (
            <Chip
              label={getActiveFilterCount()}
              size="small"
              color="primary"
              variant="filled"
            />
          )}
        </Box>
        <Box>
          <Button
            size="small"
            startIcon={<BookmarkBorderIcon />}
            onClick={(e) => setPresetMenuAnchor(e.currentTarget)}
          >
            Presets
          </Button>
          <Menu
            anchorEl={presetMenuAnchor}
            open={Boolean(presetMenuAnchor)}
            onClose={() => setPresetMenuAnchor(null)}
          >
            <MenuItem onClick={handleSavePreset}>
              <ListItemIcon>
                <BookmarkIcon />
              </ListItemIcon>
              <ListItemText primary="Save Current Filters" />
            </MenuItem>
            <Divider />
            {DEFAULT_PRESETS.map((preset) => (
              <MenuItem key={preset.id} onClick={() => handleApplyPreset(preset)}>
                <ListItemText
                  primary={preset.name}
                  secondary={preset.description}
                />
              </MenuItem>
            ))}
            {savedPresets.length > 0 && (
              <>
                <Divider />
                {savedPresets.map((preset) => (
                  <MenuItem key={preset.id} onClick={() => handleApplyPreset(preset)}>
                    <ListItemText
                      primary={preset.name}
                      secondary={preset.description}
                    />
                  </MenuItem>
                ))}
              </>
            )}
          </Menu>
        </Box>
      </Box>

      {/* Document Source */}
      <FormControl component="fieldset" sx={{ mb: 3 }}>
        <FormLabel component="legend">Document Source</FormLabel>
        <RadioGroup
          value={localFilters.documentSource}
          onChange={(e) => handleDocumentSourceChange(e.target.value as SearchScope)}
        >
          <FormControlLabel
            value="all"
            control={<Radio />}
            label="All Documents"
          />
          <FormControlLabel
            value="public"
            control={<Radio />}
            label="Public Documents Only"
          />
          <FormControlLabel
            value="private"
            control={<Radio />}
            label="My Documents Only"
          />
        </RadioGroup>
      </FormControl>

      {/* Document Categories */}
      <FormControl component="fieldset" sx={{ mb: 3 }}>
        <FormLabel component="legend">Document Categories</FormLabel>
        <FormGroup>
          {DOCUMENT_CATEGORIES.map((category) => (
            <FormControlLabel
              key={category.value}
              control={
                <Checkbox
                  checked={localFilters.categories.includes(category.value)}
                  onChange={(e) => handleCategoryChange(category.value, e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">{category.label}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {category.description}
                  </Typography>
                </Box>
              }
            />
          ))}
        </FormGroup>
      </FormControl>

      {/* Advanced Filters */}
      <Box sx={{ mb: 2 }}>
        <Button
          onClick={() => setShowAdvanced(!showAdvanced)}
          startIcon={showAdvanced ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          size="small"
        >
          Advanced Options
        </Button>
      </Box>

      <Collapse in={showAdvanced}>
        <Box sx={{ mb: 3 }}>
          {/* Date Range */}
          <FormControl component="fieldset" sx={{ mb: 3 }}>
            <FormLabel component="legend">Date Range</FormLabel>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <DatePicker
                  label="From"
                  value={localFilters.dateRange?.start || null}
                  onChange={(date) => handleDateRangeChange('start', date)}
                  slotProps={{
                    textField: { size: 'small', fullWidth: true }
                  }}
                />
                <DatePicker
                  label="To"
                  value={localFilters.dateRange?.end || null}
                  onChange={(date) => handleDateRangeChange('end', date)}
                  slotProps={{
                    textField: { size: 'small', fullWidth: true }
                  }}
                />
              </Box>
            </LocalizationProvider>
          </FormControl>
        </Box>
      </Collapse>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          startIcon={<ClearIcon />}
          onClick={handleClearFilters}
          size="small"
        >
          Clear
        </Button>
        <Button
          variant="contained"
          onClick={handleApplyFilters}
          size="small"
        >
          Apply Filters
        </Button>
      </Box>

      {/* Active Filters Display */}
      {getActiveFilterCount() > 0 && (
        <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Active Filters:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {localFilters.categories.map((category) => (
              <Chip
                key={category}
                label={DOCUMENT_CATEGORIES.find(c => c.value === category)?.label || category}
                size="small"
                onDelete={() => handleCategoryChange(category as DocumentCategory, false)}
                color="primary"
                variant="outlined"
              />
            ))}
            {localFilters.documentSource !== 'all' && (
              <Chip
                label={`Source: ${localFilters.documentSource}`}
                size="small"
                onDelete={() => handleDocumentSourceChange('all')}
                color="primary"
                variant="outlined"
              />
            )}
            {localFilters.dateRange && (
              <Chip
                label={`Date: ${localFilters.dateRange.start.toLocaleDateString()} - ${localFilters.dateRange.end.toLocaleDateString()}`}
                size="small"
                onDelete={() => setLocalFilters({ ...localFilters, dateRange: undefined })}
                color="primary"
                variant="outlined"
              />
            )}
          </Box>
        </Box>
      )}
    </Box>
  );

  if (onToggleCollapse) {
    return (
      <Paper variant="outlined" sx={{ mb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 1,
            cursor: 'pointer'
          }}
          onClick={onToggleCollapse}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterListIcon color="primary" />
            <Typography variant="subtitle1">Filters</Typography>
            {getActiveFilterCount() > 0 && (
              <Chip
                label={getActiveFilterCount()}
                size="small"
                color="primary"
                variant="filled"
              />
            )}
          </Box>
          <IconButton size="small">
            {isCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
          </IconButton>
        </Box>
        <Collapse in={!isCollapsed}>
          {filterContent}
        </Collapse>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ mb: 2 }}>
      {filterContent}
    </Paper>
  );
};